import React, { useMemo, useCallback, useState, useEffect } from "react";
import TableContainer from "../../../components/Common/TableContainer.js";
import Breadcrumbs from "../../../components/Common/Breadcrumb.js";
import { Card, CardBody, Button, Container } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { generateColumns } from "../../../custom/Utils/ColumnUtils.js";
import useFetchData from "../../../custom/Utils/UseFetchDataUtils.js";
import usePagination from "../../../custom/Utils/UsePaginationUtils.js";
import useListFilters from "../../../custom/Utils/UseListFiltersUtils.js";
import { listPageData, exportPageData } from "./ZoneFile.js";
import useDeleteRecord from "../../../custom/Utils/UseDeleteRecordUtils.js";
import DeleteConfirmModal from "../../../custom/components/Modal/DeleteConfirmModal.js";
import { getRecords } from "../../../services/api.service.js";

import SmartImportButton from "../../../custom/components/Import/SmartImportButton.js";
const ZoneList = () => {
  const navigate = useNavigate();
  const {
    payload,
    navigatePage,
    headerName,
    columnData,
    endpoint,
    searchfields,
    exportFileName,
    filterConfig,
  } = listPageData();
  const initialPayload = useMemo(() => payload, []);

  // pagination
  const {
    page,
    pageSize,
    nextPage,
    previousPage,
    lastPage,
    firstPage,
    handlePageSizeChange,
    setPage,
  } = usePagination();

  // fetch data using the provided payload, page, and page size
  // list filters (config comes from the master's *File.js)
  const {
    filters,
    filterValues,
    handleFilterChange,
    resetFilters,
    filterWhereCondition,
    appliedFilterCount,
  } = useListFilters(filterConfig);

  const { data, isLoading, setSearchKeyword, totalCount, refetch } = useFetchData(
    initialPayload,
    page,
    pageSize,
    endpoint,
    searchfields,
    { filterWhere: filterWhereCondition, onResetPage: firstPage }
  );
  const [dataForExport, setDataForExport] = useState([]);

  // handle create action
  const handleCreate = () => navigate(navigatePage.create);

  // handle edit action
  const handleEdit = useCallback(
    (id) => {
      navigate(navigatePage.edit + "/" + id);
    },
    [navigate, navigatePage.edit]
  );

  const handleView = useCallback(
    (id) => {
      navigate(navigatePage.view + "/" + id);
    },
    [navigate, navigatePage.view]
  );

  // delete flow: confirmation popup + server side dependency check
  const deleteAction = useDeleteRecord({
    modelName: payload.modelName,
    label: headerName,
    onDeleted: refetch,
  });

  // columns data
  const columns = useMemo(
    () =>
      generateColumns(data, columnData, handleEdit, handleView, page, pageSize, null, null, {
        onDelete: deleteAction.requestDelete,
      }),
    [data, columnData, handleEdit, handleView, page, pageSize, deleteAction.requestDelete]
  );

  // handle page size change callback
  const handlePageSizeChangeWrapper = useCallback(
    (newSize) => {
      handlePageSizeChange(newSize, totalCount);
    },
    [totalCount]
  );
  useEffect(() => {
    const handleExportData = async () => {
      try {
        const responseData = await getRecords(exportPageData, endpoint);
        setDataForExport(responseData.data);
      } catch (error) {
        console.error("Error fetching records:", error);
      }
    };

    handleExportData();
  }, []);

  const exportData = dataForExport?.map((item, index) => ({
    "Sr. No": index + 1,
    "MENU NAME": item?.menu_name || "N/A",
    "MENU URL": item?.menu_url || "N/A",
    "MENU ICON": item?.menu_icon || "N/A",
    "CREATED BY": item?.createdBy?.first_name || "N/A",
    STATUS:
      item?.is_active === false || item?.is_active === 0
        ? "In-active"
        : "Active",
    "IS CHILD": item?.is_child === false || item?.is_child === 0 ? "NO" : "YES",
    "IS VISIBLE ON MOBILE":
      item?.is_visible_on_mobile === false || item?.is_visible_on_mobile === 0
        ? "NO"
        : "YES",
  }));

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "10px",
            }}
          >
            <Breadcrumbs title={headerName + " List"} breadcrumbItems={[]} />
            <Button
              color="primary"
              className="waves-effect waves-light me-1"
              onClick={handleCreate}
              style={{ marginLeft: "auto", background: "#006eb9" }}
            >
              Create {headerName}
            </Button>
            <SmartImportButton
              module="zone"
              label={headerName}
              onImported={refetch}
            />
          </div>

          <Card>
            <CardBody>
              <TableContainer
                columns={columns || []}
                data={data || []}
                isLoading={isLoading}
                onSearchInputData={setSearchKeyword}
                pageIndexProp={page}
                onPageSizeChange={handlePageSizeChangeWrapper}
                nextToPage={nextPage}
                lastToPage={() => lastPage(totalCount)}
                previousToPage={previousPage}
                previousFirstToPage={firstPage}
                totalCount={totalCount}
                customPageSize={pageSize}
                filters={filters}
                filterValues={filterValues}
                onFilterChange={handleFilterChange}
                onFilterReset={resetFilters}
                appliedFilterCount={appliedFilterCount}
                exportData={exportData}
                exportFileName={exportFileName}
              />
            </CardBody>
          </Card>
          <DeleteConfirmModal {...deleteAction.modalProps} />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ZoneList;
